import React from 'react';
import { AnimatedSection } from '../components/AnimatedSection';
import { Card, CardContent } from '../components/ui/card';
import { Stethoscope, Heart, Zap, Sparkles, Shield, Activity } from 'lucide-react';

export const Medbed = () => {
  const features = [
    {
      icon: Heart,
      title: 'Advanced Diagnostics',
      description: 'Theoretical diagnostic capabilities using advanced scanning and detection technologies.',
    },
    {
      icon: Zap,
      title: 'Rapid Treatment',
      description: 'Concepts around accelerated healing processes through innovative medical approaches.',
    },
    {
      icon: Sparkles,
      title: 'Regenerative Technology',
      description: 'Exploration of cellular regeneration and tissue repair methodologies.',
    },
    {
      icon: Shield,
      title: 'Non-Invasive Procedures',
      description: 'Focus on non-surgical treatment options and gentle therapeutic approaches.',
    },
    {
      icon: Activity,
      title: 'Holistic Wellness',
      description: 'Comprehensive approach to physical and mental health optimization.',
    },
    {
      icon: Stethoscope,
      title: 'Preventive Care',
      description: 'Early detection and prevention strategies for long-term health.',
    },
  ];

  return (
    <div className="min-h-screen bg-white pt-20">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-[#0B2A4A] via-[#0B2A4A] to-[#1a3d5f] overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-[#FFD700] rounded-full filter blur-3xl animate-pulse delay-1000" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 bg-[#FFD700] rounded-full flex items-center justify-center shadow-2xl shadow-[#FFD700]/50">
              <Stethoscope className="w-10 h-10 text-[#0B2A4A]" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Medbed Technology
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Exploring futuristic healthcare innovation and medical technologies
          </p>
        </div>
      </section>

      {/* Main Content */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
              <div>
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  The Future of Healthcare
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  Medbed technology represents emerging conversations around futuristic healthcare innovation. These concepts explore how advanced medical technologies might revolutionize diagnosis, treatment, and wellness in the future.
                </p>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  The term "medbed" has gained attention in discussions about next-generation healthcare solutions that could potentially offer non-invasive treatments, advanced diagnostics, and holistic healing approaches.
                </p>
                <p className="text-lg text-gray-600 leading-relaxed">
                  While still largely theoretical, these ideas inspire research into innovative medical technologies that could improve patient outcomes and accessibility to healthcare worldwide.
                </p>
              </div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1653179675238-cc722693b666?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxmaW5hbmNpYWwlMjBpbm5vdmF0aW9ufGVufDB8fHx8MTc3MzczNTcwMHww&ixlib=rb-4.1.0&q=85"
                  alt="Medical Technology Innovation"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-[#FFD700] rounded-full filter blur-3xl opacity-20" />
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Features Grid */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-[#0B2A4A] mb-6">
                Conceptual Capabilities
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Theoretical features of advanced medical technology
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <AnimatedSection key={index} delay={index * 100}>
                  <Card className="group h-full bg-white border-2 border-transparent hover:border-[#FFD700] shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                    <CardContent className="p-8">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#0B2A4A] to-[#1a3d5f] rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:rotate-3 transition-all duration-300">
                        <feature.icon className="w-8 h-8 text-[#FFD700]" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0B2A4A] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-gray-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </CardContent>
                  </Card>
                </AnimatedSection>
              ))}
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Vision Section */}
      <AnimatedSection>
        <section className="py-24 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div className="order-2 lg:order-1 relative">
                <img
                  src="https://images.unsplash.com/photo-1758862527435-1b623cbd4206?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwzfHxmaW5hbmNpYWwlMjBpbm5vdmF0aW9ufGVufDB8fHx8MTc3MzczNTcwMHww&ixlib=rb-4.1.0&q=85"
                  alt="Healthcare Innovation"
                  className="rounded-2xl shadow-2xl transform hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute -top-6 -left-6 w-48 h-48 bg-[#0B2A4A] rounded-full filter blur-3xl opacity-10" />
              </div>
              <div className="order-1 lg:order-2">
                <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                  Healthcare Innovation Vision
                </h2>
                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                  The medbed concept envisions a future where healthcare becomes more:
                </p>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Accessible:</strong> Advanced treatments available to broader populations
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Preventive:</strong> Focus on preventing illness before it occurs
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Personalized:</strong> Treatments tailored to individual genetic profiles
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Non-Invasive:</strong> Minimizing surgical procedures and recovery time
                    </p>
                  </li>
                  <li className="flex items-start">
                    <div className="w-6 h-6 bg-[#FFD700] rounded-full flex items-center justify-center mr-4 mt-1 flex-shrink-0">
                      <span className="text-[#0B2A4A] text-sm font-bold">✓</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">
                      <strong className="text-[#0B2A4A]">Integrated:</strong> Combining physical, mental, and emotional wellness
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Current Research */}
      <AnimatedSection>
        <section className="py-24 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-[#0B2A4A] mb-6">
                Today's Medical Innovations
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                While futuristic medbeds remain conceptual, current medical technology continues advancing rapidly
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Regenerative Medicine</h3>
                <p className="text-gray-600 leading-relaxed">
                  Stem cell therapy and tissue engineering are making real progress in repairing damaged organs and tissues.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">AI-Powered Diagnostics</h3>
                <p className="text-gray-600 leading-relaxed">
                  Artificial intelligence is enhancing disease detection, analyzing medical imaging with unprecedented accuracy.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Precision Medicine</h3>
                <p className="text-gray-600 leading-relaxed">
                  Genetic profiling enables customized treatments based on individual DNA, improving effectiveness and reducing side effects.
                </p>
              </div>

              <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-[#FFD700] hover:shadow-xl transition-shadow duration-300">
                <h3 className="text-xl font-bold text-[#0B2A4A] mb-3">Robotic Surgery</h3>
                <p className="text-gray-600 leading-relaxed">
                  Minimally invasive robotic procedures offer greater precision, smaller incisions, and faster recovery times.
                </p>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>

      {/* Disclaimer */}

    </div>
  );
};

export default Medbed;
